import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AppProviders } from './app/providers/AppProviders';
import { router } from './app/Router';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { initPosthog, trackPageView } from './lib/analytics/posthog';
import { createEngagementTracker } from './lib/analytics/engagement';
import { env } from './config/env';
import './styles/tailwind.css';
import './styles/globals.css';

// Sentry error tracking + performance tracing.
// Inert unless VITE_SENTRY_DSN is set (empty DSN = disabled).
Sentry.init({
  dsn: env.sentryDsn,
  release: `asogro-frontend@${env.appVersion}`,
  environment: env.appEnv,
  tracesSampleRate: env.sentryTracesSampleRate,
  sendDefaultPii: false,
  integrations: [Sentry.browserTracingIntegration()],
});

// Measures time-on-page with active-vs-idle distinction. Flushed on every
// route change and on tab close (page_engagement events).
const engagementTracker = createEngagementTracker();

// Track a pageview whenever the active route changes. This subscribes to the
// router directly (instead of a sibling component using useLocation()) because
// hooks only work inside <RouterProvider>, which crashed the app on load.
let lastTrackedPath: string | null = null;
const trackRouteChange = (path: string) => {
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;

  const client = initPosthog({
    enableAnalytics: env.enableAnalytics,
    posthogKey: env.posthogKey,
    posthogHost: env.posthogHost,
  });

  if (client) {
    engagementTracker.handleRouteChange(path);
    trackPageView(path, {
      route: path,
      app_env: env.appEnv,
    });
  }
};

trackRouteChange(router.state.location.pathname);
router.subscribe((state) => trackRouteChange(state.location.pathname));

const AppRoot = () => {
  return <RouterProvider router={router} />;
};

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <AppProviders>
        <AppRoot />
      </AppProviders>
    </ErrorBoundary>
  </React.StrictMode>
);
